/**
 * RoleAccessService.js
 * Reads Screen_access from sessionStorage (CacheProviderService) and provides helpers for
 * filtering side menu items, tabs, and sub-tabs by viewAccess / editAccess.
 */
import { getCachevalue } from "./CacheProviderService";

// Side menu label → screen title in Screen_access JSON
const MENU_LABEL_TO_SCREEN = {
  "Billing": "Home",
  "Billing Transaction": "Billing Transaction",
  "Specimen": "Specimen",
  "Reporting": "Reporting",
  "Credit Settlement": "Credit Settlement",
  "Reports": "Reports",
  "Masters": "Masters",
};

export function getScreenAccess() {
  try {
    const raw = getCachevalue("Scrren_access");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Returns true if the side menu item (identified by its label) should be visible.
 * Falls back to true when no Screen_access is stored (admin / no restriction).
 */
export function canViewSideMenu(label) {
  const access = getScreenAccess();
  if (!access) return true;
  const screenTitle = MENU_LABEL_TO_SCREEN[label] ?? label;
  const screen = access.find((s) => s.title === screenTitle);
  return screen ? screen.viewAccess : true;
}

/**
 * Returns true if a tab / sub-tab (identified by its menuName) should be visible.
 * Searches main_tabs and sub_tabs of all screens.
 * Falls back to true when no Screen_access is stored or title is not found.
 */
export function canViewTab(menuName) {
  const access = getScreenAccess();
  if (!access) return true;
  for (const screen of access) {
    for (const tab of screen.main_tabs || []) {
      if (tab.title === menuName) return tab.viewAccess;
      for (const sub of tab.sub_tabs || []) {
        if (sub.title === menuName) return sub.viewAccess;
      }
    }
  }
  return true; // title not in access config → visible by default
}

/**
 * Returns true if a tab / sub-tab has edit access.
 */
export function canEditTab(menuName) {
  const access = getScreenAccess();
  if (!access) return true;
  for (const screen of access) {
    for (const tab of screen.main_tabs || []) {
      if (tab.title === menuName) return tab.editAccess;
      for (const sub of tab.sub_tabs || []) {
        if (sub.title === menuName) return sub.editAccess;
      }
    }
  }
  return true;
}

/** Tab title → route path (first tab in each screen used as default). */
const TAB_TITLE_TO_PATH = {
  "New Bill": "/HistopathologyHome/NewBill",
  "Existing Bill": "/HistopathologyHome/ExistingBill",
  "Overall Bills": "/HistopathologyBillingTransaction/OverallBills",
  "Credit Bills": "/HistopathologyBillingTransaction/CreditBills",
  "Receipt Bills": "/HistopathologyBillingTransaction/ReceiptBills",
  "Specimen Collection": "/HistopathologySpecimen/SpecimenCollection",
  "Outsource Test": "/HistopathologySpecimen/OutsourceTest",
  "Pending List": "/HistopathologyReporting/PendingList",
  "Approval List": "/HistopathologyReporting/ApprovalList",
  "Completed List": "/HistopathologyReporting/CompletedList",
  "Hospital Provider": "/HistopathologyCreditSettlement/CorporateProvider",
  "Hospital Settlement": "/HistopathologyCreditSettlement/CorporateProvider",
  "Settlement Report": "/HistopathologyCreditSettlement/SettlementReport",
  "Bill Report": "/HistopathologyReports/BillReport",
  "Patient Demographics Report": "/HistopathologyReports/PatientDemographicsReport",
  "Outsource Report": "/HistopathologyReports/OutsourceReport",
  "TAT Report": "/HistopathologyReports/TATReport",
  "Service Master": "/HistopathologySetting/MasterConfiguration/ServiceMaster",
  "Result Template": "/HistopathologySetting/MasterConfiguration/ResultTemplate",
  "Specimen Master": "/HistopathologySetting/MasterConfiguration/SpecimenMaster",
  "Demographics Layout": "/HistopathologySetting/ClinicConfiguration/DemographicsLayout",
  "Outsource Configuration": "/HistopathologySetting/ClinicConfiguration/OutSourceConfiguration",
  "E-Sign Configuration": "/HistopathologySetting/ClinicConfiguration/eSignConfiguration",
  "Referred Doctor": "/HistopathologySetting/ClinicConfiguration/ReferredDoctor",
  "Custom Configuration": "/HistopathologySetting/ClinicConfiguration/CustomConfiguration",
  "Hospital Configuration": "/HistopathologySetting/ClinicConfiguration/CorporateConfiguration",
  "Role Access": "/HistopathologySetting/UserConfiguration/RoleAccess",
  "User Configuration": "/HistopathologySetting/UserConfiguration/UserConfig",
};

/**
 * Returns true if the current user has view access to the given route path.
 * Resolves path → tab title via TAB_TITLE_TO_PATH, then checks viewAccess.
 * Falls back to true if no Screen_access stored or path not mapped.
 */
export function canAccessPath(pathname) {
  if (!pathname) return true;
  const access = getScreenAccess();
  if (!access) return true;
  let matchedTitle = null;
  for (const [title, p] of Object.entries(TAB_TITLE_TO_PATH)) {
    if (p === pathname || pathname.startsWith(p + "/")) { matchedTitle = title; break; }
  }
  if (!matchedTitle) return true;
  return canViewTab(matchedTitle);
}

/**
 * Returns the first route path the user has view access to from permission_access.
 * Iterates screens and main_tabs in order; returns the path for the first tab with viewAccess.
 * Falls back to /HistopathologyHome/NewBill if none or no access stored.
 */
export function getFirstAllowedRoute(screenAccess) {
  const access = Array.isArray(screenAccess) ? screenAccess : null;
  if (!access?.length) return "/HistopathologyHome/NewBill";
  for (const screen of access) {
    if (!screen.viewAccess) continue;
    const tabs = screen.main_tabs || [];
    for (const tab of tabs) {
      if (tab.viewAccess && tab.title && TAB_TITLE_TO_PATH[tab.title]) {
        return TAB_TITLE_TO_PATH[tab.title];
      }
    }
  }
  return "/HistopathologyHome/NewBill";
}
